import { logUsage } from "owa-analytics";
import { assertNever } from "owa-assert";
import { isGlobalFeatureEnabled } from "owa-feature-flags";
import isConsumer from "owa-session-store/lib/utils/isConsumer";
import { getAccountScopeUserSettings } from "owa-session-store/lib/selectors/getAccountScopeUserSettings";
import { tryGetGlobalSettingsAccountMailboxInfo } from "owa-account-source-list-store";
import { isOcpsStoreReady } from "owa-ocps-get-policies";
import { isOcpsPolicyEnabled, OcpsPolicy } from "owa-ocps-policy-store";
import { getBrowserInfo, getOs, isMinimumBrowserVersion, type Browser, type OperatingSystem } from "owa-user-agent";
import getAttachmentPolicy from "owa-session-store/lib/utils/getAttachmentPolicy";
import type { FluidOwaSource } from "../enums/FluidEnums";
import { isCollabSpaceEnabledForSource } from "./isCollabSpaceEnabledForSource";
import type { MailboxInfo } from "owa-client-types";
import { getBposNavBarData } from "owa-bpos-store";
import { isOCPSPolicyEnabledForLoopType } from "./isOCPSPolicyEnabledForLoopType";
import { LoopType } from "./constants";
import { isServiceRequestSupportedForMailbox } from "owa-service/lib/utils/isServiceRequestSupportedForMailbox";

let hasBeenChecked: boolean = false;
let isFluidEnabled: boolean | undefined = undefined;
let fluidDisabledReason: string | undefined = undefined;

export function isFluidEnabledForSource(source: FluidOwaSource, mailboxInfo: MailboxInfo, loopType: LoopType, skipCheckingUserOptions: boolean = false): boolean {
    const hasAttachmentProvider = getAttachmentPolicy(mailboxInfo)?.AttachmentDataProviderAvailable;
    const hasBPOSNavBarData = getHasBPOSNavBarData(hasAttachmentProvider, mailboxInfo);
    const hasOneDriveForBusiness = hasAttachmentProvider || hasBPOSNavBarData;
    
    const checkOneDriveForBusiness = loopType === LoopType.Fluid || loopType === LoopType.VideoRecord;
    
    let disabledReasonInternal = undefined;
    
    // once Fluid is supported in consumer, we want to continue restricting feature access to Cloud Cache users
    // therefore replace isConsumer() with getUserConfiguration().SessionSettings?.IsShadowMailbox
    // For preload we check if fluid is enabled before options are loaded.
    // Fall back to global settings account for cross-tenant contexts (e.g. GDAP) where
    // the mailboxInfo can't be resolved in the session store.
    const isMailboxSettingsResolved = !!getAccountScopeUserSettings(mailboxInfo)?.SessionSettings;
    let mailboxInfoForConsumerCheck = mailboxInfo;
    let fallbackMailboxInfoSelected = false;
    if (!isMailboxSettingsResolved) {
        const globalMailboxInfo = tryGetGlobalSettingsAccountMailboxInfo(false);
        if (globalMailboxInfo) {
            mailboxInfoForConsumerCheck = globalMailboxInfo;
            fallbackMailboxInfoSelected = true;
        }
    }
    if (!isServiceRequestSupportedForMailbox(mailboxInfo)) {
        disabledReasonInternal = "ServiceRequestNotSupportedForMailbox";
    }
    else if (isConsumer(undefined /* smtpAddress */, mailboxInfoForConsumerCheck)) {
        disabledReasonInternal = "Consumer";
    }
    else if (!hasOneDriveForBusiness && checkOneDriveForBusiness) {
        disabledReasonInternal = "NoOneDriveForBusiness";
    }
    else if (!isBrowserSupported()) {
        disabledReasonInternal = "BrowserNotSupported";
    }
    else if (!(isOCPSPolicyEnabledForLoopType(loopType, mailboxInfo) || skipCheckingUserOptions)) {
        if (isOcpsStoreReady()) {
            disabledReasonInternal = "OcpsPolicyDisabled";
        }
        else {
            disabledReasonInternal = "OcpsPolicyNotReady";
        }
    }
    
    if (!!disabledReasonInternal) {
        if (!getAttachmentPolicy(mailboxInfo)) {
            logUsage("FluidEnabledAttachmentPolicyUndefined", mailboxInfo, { source });
        }
        logUsage("FluidNotEnabled", mailboxInfo, {
            source,
            isAttachmentPolicyEnabled: hasAttachmentProvider,
            isBrowserSupported: isBrowserSupported(),
            browser: getBrowserInfo().browser,
            isOcpsPolicyEnabled: isOcpsPolicyEnabled(OcpsPolicy.LoopEnabledForOutlook, true, mailboxInfo) &&
                isOcpsPolicyEnabled(OcpsPolicy.Loop, true, mailboxInfo),
            isOcpsReady: isOcpsStoreReady(),
            isConsumer: isConsumer(undefined /* smtpAddress */, mailboxInfoForConsumerCheck),
            usedFallbackMailboxInfo: !isMailboxSettingsResolved,
            hasBPOSNavBarData,
            loopType,
            disabledReasonInternal,
            isPreload: skipCheckingUserOptions,
            ...getMeetingNotesDiagnostics(source, loopType, disabledReasonInternal, fallbackMailboxInfoSelected),
        });
        logFluidEnabledStatus(false /* isEnabled */, disabledReasonInternal, source, loopType, mailboxInfo, !isMailboxSettingsResolved, fallbackMailboxInfoSelected);
        return false;
    }
    
    switch (source) {
        case "MailReadingPane":
        case "MailCompose":
        case "CalendarEventBody":
        case "ForwardEventEditor":
        // Meeting Briefing is gated upstream by its own cal-meetingBriefing* flights, so it only
        // needs the generic Loop check here. It is deliberately not grouped with CalendarCompose,
        // which is additionally gated on meeting-notes flags that do not apply to briefings.
        case "MeetingBriefing":
        case "None":
            if (isGlobalFeatureEnabled("cmp-prague")) {
                logFluidEnabledStatus(true /* isEnabled */, undefined /* disabled reason */, source, loopType, mailboxInfo, !isMailboxSettingsResolved);
                return true;
            }
            logUsage("FluidNotEnabledFromFlightCheck", mailboxInfo, { source });
            logFluidEnabledStatus(false /* isEnabled */, "flightCheck" /* disabled reason */, source, loopType, mailboxInfo, !isMailboxSettingsResolved);
            return false;
        case "CalendarReadingPane":
        case "MailCalendarCard":
        case "CalendarCompose":
            const isCollabSpaceEnabled = isCollabSpaceEnabledForSource(source, mailboxInfo);
            const disabledReason = isCollabSpaceEnabled ? undefined : "flightCheck";
            logFluidEnabledStatus(isCollabSpaceEnabled /* isEnabled */, disabledReason, source, loopType, mailboxInfo, !isMailboxSettingsResolved, fallbackMailboxInfoSelected);
            return isCollabSpaceEnabled;
        case "MOS":
            const isEnabled = isGlobalFeatureEnabled("mos-hoopApp");
            const disabledReasonMOS = isEnabled ? undefined : "flightCheck";
            logFluidEnabledStatus(isEnabled, disabledReasonMOS, source, loopType, mailboxInfo, !isMailboxSettingsResolved);
            return isEnabled;
        case "NewsletterCompose":
        case "NewsletterPreview":
            const isEnabledNewsletters = isGlobalFeatureEnabled("addison-loops");
            const disabledReasonNewsletters = isEnabledNewsletters ? undefined : "flightCheck";
            logFluidEnabledStatus(isEnabledNewsletters, disabledReasonNewsletters, source, loopType, mailboxInfo, !isMailboxSettingsResolved);
            return isEnabledNewsletters;
        default:
            return assertNever(source);
    }
}

function isBrowserSupported(): boolean {
    const browser: Browser = getBrowserInfo().browser;
    switch (browser) {
        case "Chrome":
        case "Edge Anaheim":
            return isMinimumBrowserVersion([120]);
        case "Firefox":
            return isMinimumBrowserVersion([115]);
        case "Opera":
            return true;
        case "Safari":
            return getOs() !== "Windows" && isMinimumBrowserVersion([16, 0]);
        default:
            return false;
    }
}

function getHasBPOSNavBarData(hasAttachmentProvider: boolean | undefined, mailboxInfo: MailboxInfo): boolean {
    const bposNavBarData = getBposNavBarData();
    const hasNavBarData = !!bposNavBarData?.SPO_RootSiteUrl;
    if (hasNavBarData !== hasAttachmentProvider) {
        logUsage("FluidEnabledCheckNavBarDataMismatch", mailboxInfo, {
            hasNavBarData,
            hasAttachmentProvider,
        });
    }
    return hasNavBarData;
}

function logFluidEnabledStatus(isEnabled: boolean, reason: string | undefined, source: FluidOwaSource, loopType: LoopType, mailboxInfo: MailboxInfo, usedFallbackMailboxInfo?: boolean, fallbackMailboxInfoSelected: boolean = false) {
    if (hasBeenChecked && (isFluidEnabled !== isEnabled || reason !== fluidDisabledReason)) {
        logUsage("FluidEnabledStatus", mailboxInfo, {
            isNowEnabled: isEnabled,
            wasPreviouslyEnabled: isFluidEnabled,
            newDisabledReason: reason,
            previousDisabledReason: fluidDisabledReason,
            source,
            loopType,
            usedFallbackMailboxInfo,
            ...getMeetingNotesDiagnostics(source, loopType, reason, fallbackMailboxInfoSelected),
        });
    }
    hasBeenChecked = true;
    isFluidEnabled = isEnabled;
    fluidDisabledReason = reason;
}

function getMeetingNotesDiagnostics(source: FluidOwaSource, loopType: LoopType, reason: string | undefined, fallbackMailboxInfoSelected: boolean) {
    if ((source === "CalendarCompose" || source === "CalendarReadingPane") &&
        loopType === LoopType.Fluid &&
        isGlobalFeatureEnabled("cal-fluid-meetingNotesDiagnostics")) {
        return {
            evaluatedGate: getEligibilityGate(reason),
            fallbackMailboxInfoSelected,
        };
    }
    return undefined;
}

function getEligibilityGate(reason: string | undefined) {
    switch (reason) {
        case undefined:
            return "None";
        case "ServiceRequestNotSupportedForMailbox":
            return "ServiceRequest";
        case "Consumer":
            return "Consumer";
        case "NoOneDriveForBusiness":
        case "OneDriveDataNotReady":
            return "OneDrive";
        case "BrowserNotSupported":
            return "Browser";
        case "OcpsPolicyDisabled":
        case "OcpsPolicyNotReady":
            return "Ocps";
        case "flightCheck":
            return "SourceFlight";
        default:
            return "Unknown";
    }
}
